<template>
  <div class="confirmation-root">
    <confirmation-block
      class="confirmation-block"
      data-cy="withdraw-to-card-confirmation-coin-amount"
    >
      <template #title>
        {{ $t('WithdrawToCard | confirmationFromTitle') }}
      </template>

      <template #description>
        <div class="coin-amount-row">
          <span class="coin-amount">{{ coinAmount }}</span>
          <span class="coin-title">{{ coin?.title }}</span>
        </div>
      </template>

      <template #additional-content>
        <div class="fiat-amount-row">
          {{ formatFiatAmount(fiatAmount) }}
        </div>
      </template>
    </confirmation-block>

    <confirmation-block
      class="confirmation-block"
      data-cy="withdraw-to-card-confirmation-card"
    >
      <template #title>
        {{ $t('WithdrawToCard | confirmationToTitle') }}
      </template>

      <template #description>
        {{ formattedCardNumber || formattedPhoneNumber }}
      </template>

      <template #additional-content>
        <svg-icon :name="paymentMethod?.icon" class="payment-method-icon" />
      </template>
    </confirmation-block>

    <withdraw-info
      class="info-block"
      :is-loading-rate="isLoadingRate"
      :rate="rate"
      :rate-time-to-live="rateTimeToLive"
      :coin="coin"
      :fiat="fiat"
    />

    <div class="actions">
      <transition name="fade">
        <base-callout
          v-if="errorCode"
          class="callout"
          type="error"
          @close="$emit('clear-error')"
        >
          {{ $t(errorCode) || $t('Something went wrong, please try again') }}
        </base-callout>
      </transition>

      <base-button
        type="cta"
        size="lg"
        :disabled="isLoadingRate || isWithdrawInProgress"
        @onClick="handleConfirm"
      >
        {{ $t('Confirm') }}
      </base-button>
    </div>
  </div>
</template>

<script>
import Utils from '@emcd-shared/utils/formatters'
import WithdrawInfo from './WithdrawInfo.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'
import { ConfirmationBlock } from '@/shared/ui/ConfirmationBlock'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import BaseCallout from '@/shared/ui/BaseCallout/BaseCallout.vue'
import { formatCardNumber } from '@/shared/lib/formatCardNumber'

export default {
  components: {
    BaseCallout,
    SvgIcon,
    BaseButton,
    WithdrawInfo,
    ConfirmationBlock,
  },

  props: {
    // How much coin user would like to withdraw
    coinAmount: {
      type: String,
    },
    // How much user will receive in fiat
    fiatAmount: {
      type: String,
    },
    // Selected coin - object with full info from coin/config
    coin: {
      type: Object,
    },
    phoneNumber: {
      type: String,
    },
    // If we right now is loading a new rate value or performing withdraw or something else
    isLoadingRate: {
      type: Boolean,
      default: false,
    },
    // If withdraw is in progress already
    isWithdrawInProgress: {
      type: Boolean,
      default: false,
    },
    // Exchange rate for coin to fiat withdraw
    rate: {
      type: Number,
    },
    // Time to update withdraw rate
    rateTimeToLive: {
      type: Number,
    },
    // Information about selected fiat currency
    fiat: {
      type: Object,
    },
    // Selected payment method
    paymentMethod: {
      type: Object,
    },
    // Card number
    cardTitle: {
      type: String,
    },
    // Code of the error from API if we have got one
    errorCode: [String, null],
  },

  computed: {
    formattedCardNumber() {
      return formatCardNumber(this.cardTitle)
    },

    formattedPhoneNumber() {
      return `+${this.phoneNumber}`
    },
  },

  methods: {
    formatFiatAmount(value) {
      return Utils.formatCurrency(
        value,
        this.fiat.value,
        2,
        false,
        'floor',
        {},
        this.$i18n.locale
      )
    },

    handleConfirm() {
      this.$emit('confirm')
    },
  },
}
</script>

<style scoped lang="scss">
.confirmation-block:not(:first-child) {
  margin-top: 10px;
}

.coin-amount-row {
  display: flex;
  justify-content: center;
  gap: 5px;
}

.coin-title {
  color: var(--text-6);
}

.fiat-amount-row {
  color: var(--text-6);
}

.payment-method-icon {
  width: 50px;
  height: 50px;
}

.info-block {
  margin-top: 60px;
}

.actions {
  margin-top: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.callout {
  position: absolute;
  top: -60px;
  left: 0;
  right: 0;
  margin: 0 auto;
  z-index: 1;
}
</style>
